import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { AppRoutes, toProjectSlug } from '../../../app-routes';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { MessageBarType } from '@fluentui/react';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { AdminTimeBlockApiClient } from '../../../../services/admin-time-block';
import { IProjectListing } from '../../../../entities-generated/models/project/project-listing';
import { ILaunchProjectAssessmentSchedulingPanelArgs, ProjectAssessmentSchedulingPanel } from '../../components/assessment-scheduling-panel';
import { where } from '../../../../framework/utils/collection';
import { ProjectStatus } from '../../../../entities-generated/common/project-status';
import { ProjectApiClient } from '../../../../services/project';
import { TimeBlockStatus } from '../../../../entities-generated/common/time-block-status';
import { ProjectAssessmentApiClient } from '../../../../services/project-assessment';
import { IAssessmentBookingRequest } from '../../../../entities-generated/models/project/assessment-booking-request';
import { IRescheduleAssessmentRequest } from '../../../../entities-generated/models/project/reschedule-assessment-request';
import { useStandardErrorState } from '../../../../entities/view-manager/error';
import { ProjectListItem } from '../../components/project-listing';
import { BwjConfirmationDialog, IConfirmationDialogDisplayProps } from '../../../../framework/components/bwj-confirmation-dialog';
import { useNavigator } from '../../../../framework/services/navigation';
import { DAY_IN_MIILISECONDS, getUnixTimestamp, getUtcNowUnixTimestamp, HOUR_IN_MILLISECONDS } from '../../../../framework/utils/date-time';
import { ProjectCancellationPanel } from '../../components/cancellation-panel';
import { IProjectCancellationPanelLaunchArgs } from '../../entities/cancellation-args';
import { EventNotificationService } from '../../../../framework/services/event-notification';
import { ProjectEvents } from '../../events';
import * as cancelFormService from '../../form-services/cancellation-editor';
import { CustomerPanel } from '../../components/customer-panel';
import { ICustomerPanelLaunchArgs } from '../../entities/customer-args';
import { BwjEventArgs } from '../../../../framework/entities/bwj-event';

const getToday = () => {
  const now = new Date();
  const today = getUnixTimestamp(now.getFullYear(), now.getMonth(), now.getDate());
  return today;
}
const getTommorow = () => getToday() + (HOUR_IN_MILLISECONDS * 24);
const isToday = (timestamp: number) => {
  const min = getToday();
  const max = getTommorow();
  return timestamp >= min && timestamp < max;
}

export const PageOpenProjects: React.FunctionComponent = () => {
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [projects, setProjects] = useState<IProjectListing[]>([]);
  const [launchAssessmentSchedulerArgs, setLaunchAssessmentSchedulerArgs] =
    useState<ILaunchProjectAssessmentSchedulingPanelArgs | undefined>(undefined);
  const [launchConfirmationDialogArgs, setLaunchConfirmationDialogArgs]
    = useState<BwjEventArgs<IConfirmationDialogDisplayProps | undefined> | undefined>(undefined);
  const loadingMessage = useRef('Loading projects...');
  const { showToast } = useToast();
  const errorState = useStandardErrorState();
  const nav = useNavigator();

  const unscheduledAssessments =
    useMemo(() => where(projects, p => p.status === ProjectStatus.AssessmentRequested), [projects]);
  const pastDueAssessments = useMemo(() => {
    const now = getToday();
    const pda = where(projects, p => p.status === ProjectStatus.AssessmentScheduled && p.assessmentScheduledDate < now);
    return pda;
  }, [projects]);
  const todayAssessments = useMemo(() => {
    const pda = where(projects, p => p.status === ProjectStatus.AssessmentScheduled && isToday(p.assessmentScheduledDate));
    return pda;
  }, [projects]);
  const upcomingAssessments = useMemo(() => {
    const pda = where(projects, p => p.status === ProjectStatus.AssessmentScheduled && getTommorow() <= p.assessmentScheduledDate);
    return pda;
  }, [projects]);
  const recentlyAssessed = useMemo(() => {
    const pda = where(projects, p => p.status === ProjectStatus.CatalogPublished);
    return pda;
  }, [projects]);
  const underConsideration = useMemo(() => {
    const pda = where(projects, p => p.status === ProjectStatus.ProjectInPlanning);
    return pda;
  }, [projects]);
  const readyForPurchase = useMemo(() => {
    const pda = where(projects, p => p.status === ProjectStatus.ProjectPlanned);
    return pda;
  }, [projects]);
  const upcomingInstallations = useMemo(() => {
    const pda = where(projects, p => p.status === ProjectStatus.InstallationScheduled && getTommorow() <= p.installationScheduledDate);
    return pda;
  }, [projects]);
  const todayInstallations = useMemo(() => {
    const pda = where(projects, p => p.status === ProjectStatus.InstallationScheduled && isToday(p.installationScheduledDate));
    return pda;
  }, [projects]);

  const showLoadingView = (message: string) => {
    setViewState(BwjViewState.Loading);
    loadingMessage.current = message;
  }

  const refreshProjects = useCallback(() => {
    showLoadingView('Loading projects...');

    ProjectApiClient.getActiveProjectList()
    .then(resp =>  {
      setProjects(resp);
      const vs = resp.length === 0 ? BwjViewState.NoSearchResults : BwjViewState.Ready;
      setViewState(vs);
    })
    .catch((e) => {
      console.log(e);
      setViewState(BwjViewState.Error);
    });
  }, []);

  const onScheduleInstallation = (project: IProjectListing, isRescheduling: boolean) => {};
  const onRecordInstallation = (project: IProjectListing) => {};
  const onEditInstallation = (project: IProjectListing) => {};
  const onCancelInstallation = (project: IProjectListing) => {};
  const onViewTransactions = (project: IProjectListing) => {};
  
  const onLaunchAssessmentScheduler = (project: IProjectListing, isRescheduling: boolean) => {
    showLoadingView('Preparing scheduler...');

    AdminTimeBlockApiClient.getManageableTimeBlocks()
    .then(resp => {
      const useableBillableDays = getUtcNowUnixTimestamp() + (DAY_IN_MIILISECONDS * 4);
      const assessmentTime = where(resp, t => {
        return t.status === TimeBlockStatus.Available
          && (t.isBillableTime === false || t.date < useableBillableDays);
      });
      const launchArgs = {
        projectId: project.id,
        isReschedulingAssessment: isRescheduling,
        schedulingOptions: assessmentTime
      };
      setLaunchAssessmentSchedulerArgs(launchArgs);
    })
    .catch((e) => {
      console.log(e);
      showToast(MessageBarType.error, 'Error - assessment scheduling not available');
    })
    .finally(() => setViewState(BwjViewState.Ready));
  };
  const onScheduleAssessment = (projectId: string, request: IAssessmentBookingRequest) => {
    showLoadingView('Scheduling assessment...');

    ProjectAssessmentApiClient.bookProjectAssessment(request, projectId)
    .then(resp => {
      setProjects(resp);
      showToast(MessageBarType.success, 'Assessment successfully scheduled');
    })
    .catch((e) => {
      console.log(e);
      showToast(MessageBarType.error, 'An unexpected error occurred');
    })
    .finally(() => setViewState(BwjViewState.Ready));
  };
  const onRescheduleAssessment = (projectId: string, request: IRescheduleAssessmentRequest) => {
    showLoadingView('Rescheduling assessment...');

    ProjectAssessmentApiClient.rescheduleProjectAssessment(request, projectId)
    .then(resp => {
      setProjects(resp);
      showToast(MessageBarType.success, 'Assessment successfully rescheduled');
    })
    .catch((e) => {
      console.log(e);
      showToast(MessageBarType.error, 'An unexpected error occurred');
    })
    .finally(() => setViewState(BwjViewState.Ready));
  };

  const markCustomerUnresponsive = (project: IProjectListing) => {
    showLoadingView('Updating customer info...');

    ProjectAssessmentApiClient.markCustomerUnresponsive(project.id)
    .then(() => {
      showToast(MessageBarType.success, 'Customer info updated');
      refreshProjects();
    })
    .catch((e) => {
      console.log(e);
      showToast(MessageBarType.error, 'Error on customer update');
      setViewState(BwjViewState.Ready);
    });
  };
  const onMarkCustomerUnresponsive = (project: IProjectListing) => {
    const args = {
      title: 'Mark Customer Unresponsive',
      message: 'This means you have reached out to the customer several times without reply - is that the case?',
      proceedPrompt: 'Yes - Unresponsive',
      cancelPrompt: 'No - Cancel',
      onProceedRequested: markCustomerUnresponsive,
      onCancelRequested: () => {},
      context: project
    };
    setLaunchConfirmationDialogArgs(new BwjEventArgs(args));
  };

  const onRecordAssessment = (project: IProjectListing) => {
    const projectId = toProjectSlug(project.id);
    nav.goTo(AppRoutes.recordCompletedAssessment.url(projectId));
  };

  const onManageIncentives = (project: IProjectListing) => {
    const projectId = toProjectSlug(project.id);
    nav.goTo(AppRoutes.managePurchaseIncentives.url(projectId));
  };

  const onEditAssessment = (project: IProjectListing) => onRecordAssessment(project);

  const onConfirmAssessmentCancellation = (project: IProjectListing) => {
    const args: IProjectCancellationPanelLaunchArgs = {
      id: project.id,
      title: 'Cancel Project Assessment'
    };
    EventNotificationService.dispatch(ProjectEvents.ProjectCancellationPanel_Launch, args);
  };

  const onCancelAssessment = (projectId: string) => {
    setViewState(BwjViewState.Loading);

    const request = cancelFormService.toCancelRequest();
    ProjectAssessmentApiClient.recordProjectAssessment(request, projectId)
    .then(() => {
      showToast(MessageBarType.info, 'Project assessment is cancelled');
      refreshProjects();
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'Error - cancellation failed');
      setViewState(BwjViewState.Ready);
    });
  };

  const onEditCustomer = (project: IProjectListing) => {
    const args: ICustomerPanelLaunchArgs = {
      projectId: project.id
    };
    EventNotificationService.dispatch(ProjectEvents.CustomerPanel_Launch, args);
  };

  const onViewProjectOverview = (project: IProjectListing) => {
    const projectId = toProjectSlug(project.id);
    nav.goTo(AppRoutes.projectOverview.url(projectId));
  };

  useEffect(() => refreshProjects(), [refreshProjects]);

  const getListingProps = (project: IProjectListing) => {
    const listingProps = {
      key: project.id,
      project,
      onEditCustomer,
      onScheduleAssessment: onLaunchAssessmentScheduler,
      onRecordAssessment,
      onEditAssessment,
      onCancelAssessment: onConfirmAssessmentCancellation,
      onManageIncentives,
      onViewProjectOverview,
      onMarkCustomerUnresponsive,
      onScheduleInstallation,
      onRecordInstallation,
      onEditInstallation,
      onCancelInstallation,
      onViewTransactions,
    };
    return listingProps;
  }

  return (
    <>
    <CustomerPanel />
    <ProjectCancellationPanel onProceedWithAction={onCancelAssessment} />
    <BwjConfirmationDialog displayArgs={launchConfirmationDialogArgs} />
    <ProjectAssessmentSchedulingPanel
      launchPanelArgs={launchAssessmentSchedulerArgs}
      onScheduleAssessment={onScheduleAssessment}
      onRescheduleAssessment={onRescheduleAssessment}
    />
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: loadingMessage.current
      }}
      errorState={errorState}
      noSearchResultsState={{
        icon: AppRoutes.openProjects.icon,
        message: 'There are currently no open projects'
      }}
    >
      <BwjPageHeader
        title={'Open Projects'}
        titleIconName={AppRoutes.openProjects.icon}
        actionButtons={[{
          id: 'btnRefresh',
          name: 'Refresh all',
          iconName: 'Refresh',
          onClick: refreshProjects
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        {unscheduledAssessments.length > 0 && <>
        <h2>Unscheduled Assessments</h2>
        {unscheduledAssessments.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
        {pastDueAssessments.length > 0 && <>
        <h2>Past Due Assessments</h2>
        {pastDueAssessments.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
        {todayAssessments.length > 0 && <>
        <h2>Today&#39;s Assessments</h2>
        {todayAssessments.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
        {todayInstallations.length > 0 && <>
        <h2>Today&#39;s Installations</h2>
        {todayInstallations.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
        {upcomingAssessments.length > 0 && <>
        <h2>Upcoming Assessments</h2>
        {upcomingAssessments.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
        {upcomingInstallations.length > 0 && <>
        <h2>Upcoming Installations</h2>
        {upcomingInstallations.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
        {recentlyAssessed.length > 0 && <>
        <h2>Recently Assessed</h2>
        {recentlyAssessed.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
        {underConsideration.length > 0 && <>
        <h2>Under Consideration</h2>
        {underConsideration.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
        {readyForPurchase.length > 0 && <>
        <h2>Ready for Purchase</h2>
        {readyForPurchase.map(project =>
        <ProjectListItem {...getListingProps(project)} />
        )}
        </>}
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};
